import { Component, inject, OnInit, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { RouterModule } from '@angular/router';

@Component({
  selector: 'app-terms',
  standalone: true,
  imports: [RouterModule],
  template: `
    <div class="legal-page">
      <header class="legal-header">
        <div class="legal-header-inner">
          <a routerLink="/" class="header-brand">
            K<span class="brand-accent">ΛI</span>ZENIC
          </a>
          <a routerLink="/" class="back-link">
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <line x1="19" y1="12" x2="5" y2="12" />
              <polyline points="12 19 5 12 12 5" />
            </svg>
            Back
          </a>
        </div>
      </header>

      <main class="legal-main">
        <div class="legal-title-section">
          <h1>Terms of Use</h1>
          <p class="legal-meta">
            <strong>Last Updated:</strong> August 4, 2025
          </p>
        </div>

        <section id="acceptance" class="terms-section">
          <h2>1. Acceptance of Terms</h2>
          <p>
            Welcome to Kaizenic ("we," "our," or "us"). These Terms of Use
            ("Terms") govern your access to and use of the Kaizenic platform,
            including our website, mobile applications, and related services
            (collectively, the "Service"). By accessing or using our Service,
            you agree to be bound by these Terms.
          </p>
          <p>
            If you do not agree to these Terms, you may not access or use our
            Service. We may modify these Terms at any time, and such
            modifications will be effective immediately upon posting. Your
            continued use of the Service after any such changes constitutes your
            acceptance of the new Terms.
          </p>
        </section>

        <section id="description" class="terms-section">
          <h2>2. Description of Service</h2>
          <p>
            Kaizenic is an AI-powered personalized learning platform featuring
            K<span class="brand-accent">ΛI</span>, your intelligent learning
            co-navigator. Our Service provides:
          </p>
          <ul>
            <li>
              <strong>Hyper-Personalized Learning Paths:</strong> AI-generated
              curricula tailored to your specific goals, learning style, and
              progress
            </li>
            <li>
              <strong>AI Learning Companion:</strong> Interactive guidance,
              Socratic dialogue, and real-time feedback through K<span
                class="brand-accent"
                >ΛI</span
              >
            </li>
            <li>
              <strong>Adaptive Content Generation:</strong> Custom learning
              modules with multimedia content and assessments
            </li>
            <li>
              <strong>Progress Tracking:</strong> Comprehensive analytics and
              competency mapping
            </li>
            <li>
              <strong>Goal Management:</strong> AI-assisted articulation,
              tracking, and achievement of learning objectives
            </li>
            <li>
              <strong>Resource Curation:</strong> Intelligent integration of
              external learning materials
            </li>
          </ul>
        </section>

        <section id="eligibility" class="terms-section">
          <h2>3. Eligibility and Account Registration</h2>

          <h3>3.1 Age Requirements</h3>
          <p>
            You must be at least 13 years old to use our Service. If you are
            between 13 and 18 years old, you may only use the Service with the
            involvement and consent of a parent or guardian.
          </p>

          <h3>3.2 Account Registration</h3>
          <p>
            To access certain features of our Service, you must create an
            account. You agree to:
          </p>
          <ul>
            <li>
              Provide accurate, current, and complete information during
              registration
            </li>
            <li>Maintain and promptly update your account information</li>
            <li>
              Maintain the security and confidentiality of your login
              credentials
            </li>
            <li>Accept responsibility for all activities under your account</li>
            <li>
              Notify us immediately of any unauthorized use of your account
            </li>
          </ul>

          <h3>3.3 Account Termination</h3>
          <p>
            You may terminate your account at any time through your account
            settings. We may suspend or terminate your account if you violate
            these Terms or engage in activities that harm our Service or other
            users.
          </p>
        </section>

        <section id="acceptable-use" class="terms-section">
          <h2>4. Acceptable Use Policy</h2>

          <h3>4.1 Permitted Uses</h3>
          <p>
            You may use our Service for legitimate educational and learning
            purposes in accordance with these Terms.
          </p>

          <h3>4.2 Prohibited Activities</h3>
          <p>You agree not to:</p>
          <ul>
            <li>
              <strong>Violate Laws:</strong> Use the Service for any unlawful
              purpose or in violation of any applicable laws
            </li>
            <li>
              <strong>Infringe Rights:</strong> Violate intellectual property
              rights, privacy rights, or other rights of third parties
            </li>
            <li>
              <strong>Harmful Content:</strong> Upload, post, or transmit
              harmful, offensive, discriminatory, or illegal content
            </li>
            <li>
              <strong>System Interference:</strong> Attempt to gain unauthorized
              access, disrupt, or interfere with the Service
            </li>
            <li>
              <strong>Automated Access:</strong> Use bots, scrapers, or
              automated tools without permission
            </li>
            <li>
              <strong>Commercial Misuse:</strong> Use the Service for commercial
              purposes without authorization
            </li>
            <li>
              <strong>False Information:</strong> Provide false, misleading, or
              deceptive information
            </li>
            <li>
              <strong>AI Manipulation:</strong> Attempt to manipulate, reverse
              engineer, or exploit K<span class="brand-accent">ΛI</span> for
              unintended purposes
            </li>
            <li>
              <strong>Academic Dishonesty:</strong> Use the Service to
              facilitate cheating, plagiarism, or other forms of academic
              misconduct
            </li>
          </ul>

          <h3>4.3 Content Standards</h3>
          <p>
            Any content you provide must be truthful, respectful of others'
            rights, appropriate for an educational environment, and free from
            viruses or harmful code.
          </p>
        </section>

        <section id="intellectual-property" class="terms-section">
          <h2>5. Intellectual Property Rights</h2>

          <h3>5.1 Our Intellectual Property</h3>
          <p>
            The Service, including all content, features, functionality, AI
            models, algorithms, and technology, is owned by Kaizenic and
            protected by copyright, trademark, patent, and other intellectual
            property laws. You may not copy, modify, distribute, reverse
            engineer, or create derivative works of our content without
            permission.
          </p>

          <h3>5.2 Your Content</h3>
          <p>
            You retain ownership of content you create or upload. By using our
            Service, you grant us a worldwide, non-exclusive, royalty-free
            license to use, reproduce, modify, and distribute your content
            solely for providing and improving the Service.
          </p>

          <h3>5.3 AI-Generated Content</h3>
          <p>
            Content generated by K<span class="brand-accent">ΛI</span>
            specifically for your learning is provided for your personal
            educational use. You may use this content for your learning purposes
            but may not redistribute it commercially without permission.
          </p>

          <h3>5.4 Third-Party Content</h3>
          <p>
            Our Service may include content from third parties. Such content
            remains the property of its respective owners and is subject to
            their terms and conditions.
          </p>
        </section>

        <section id="subscription-payment" class="terms-section">
          <h2>6. Subscription and Payment Terms</h2>

          <h3>6.1 Free Trial</h3>
          <p>
            We offer a 7-day free trial for new users. During the trial period,
            you have access to premium features at no cost. You may cancel at
            any time during the trial without charge.
          </p>

          <h3>6.2 Subscription Plans</h3>
          <p>
            After your free trial, continued access to premium features requires
            a paid subscription. Subscription fees are charged in advance on a
            recurring basis (monthly or annually).
          </p>

          <h3>6.3 Payment Processing</h3>
          <p>
            Payments are processed by secure third-party payment processors. By
            providing payment information, you authorize us to charge the
            applicable fees to your chosen payment method.
          </p>

          <h3>6.4 Automatic Renewal</h3>
          <p>
            Subscriptions automatically renew unless you cancel before the
            renewal date. You will be charged the then-current subscription fee.
          </p>

          <h3>6.5 Cancellation</h3>
          <p>
            You may cancel your subscription at any time through your account
            settings. Cancellation takes effect at the end of your current
            billing period.
          </p>

          <h3>6.6 Refunds</h3>
          <p>
            We generally do not provide refunds for subscription fees. However,
            we may provide refunds at our discretion in cases of technical
            issues or billing errors.
          </p>

          <h3>6.7 Price Changes</h3>
          <p>
            We may change subscription prices with 30 days' advance notice.
            Price changes will not affect your current billing period.
          </p>
        </section>

        <section id="ai-terms" class="terms-section">
          <h2>7. AI and Machine Learning Terms</h2>

          <h3>
            7.1 K<span class="brand-accent">ΛI</span> AI Learning Co-Navigator
          </h3>
          <p>
            K<span class="brand-accent">ΛI</span> is an artificial intelligence
            system designed to enhance your learning experience. You acknowledge
            that:
          </p>
          <ul>
            <li>
              K<span class="brand-accent">ΛI</span> provides educational
              guidance and should not replace human instruction entirely
            </li>
            <li>AI-generated content may contain errors or inaccuracies</li>
            <li>
              You should verify important information from authoritative sources
            </li>
            <li>
              K<span class="brand-accent">ΛI</span>'s responses are based on
              training data and may not reflect the most current information
            </li>
          </ul>

          <h3>7.2 Learning Data Usage</h3>
          <p>
            Your interactions with K<span class="brand-accent">ΛI</span> and
            learning data may be used to personalize your experience, improve
            our AI models (using anonymized data), and generate personalized
            content and recommendations.
          </p>

          <h3>7.3 AI Limitations</h3>
          <p>
            You understand that AI systems have limitations and may make
            mistakes, exhibit biases, have difficulty with highly specialized
            topics, and require human oversight for critical decisions.
          </p>

          <h3>7.4 Responsible AI Use</h3>
          <p>
            You agree to use K<span class="brand-accent">ΛI</span> responsibly
            and not attempt to generate harmful content, circumvent safety
            measures, use it for academic dishonesty, or exploit the AI system
            for unintended purposes.
          </p>
        </section>

        <section id="privacy-data" class="terms-section">
          <h2>8. Privacy and Data Protection</h2>
          <p>
            Your privacy is important to us. Our collection, use, and protection
            of your personal information is governed by our
            <a routerLink="/privacy">Privacy Policy</a>, which is incorporated
            into these Terms by reference.
          </p>

          <h3>8.1 Data Security</h3>
          <p>
            We implement reasonable security measures to protect your data, but
            cannot guarantee absolute security. You are responsible for
            maintaining the confidentiality of your account credentials.
          </p>

          <h3>8.2 Data Retention</h3>
          <p>
            We retain your data as described in our Privacy Policy. You may
            request deletion of your data, subject to legal and operational
            requirements.
          </p>
        </section>

        <section id="disclaimers" class="terms-section">
          <h2>9. Disclaimers and Limitations</h2>

          <h3>9.1 Service Availability</h3>
          <p>
            We strive to maintain Service availability but do not guarantee
            uninterrupted access. The Service may be temporarily unavailable due
            to maintenance, updates, or technical issues.
          </p>

          <h3>9.2 Educational Disclaimer</h3>
          <p>
            Our Service is designed for educational purposes and should
            supplement, not replace, formal education or professional
            instruction. We make no guarantees about learning outcomes or job
            placement.
          </p>

          <h3>9.3 Limitation of Liability</h3>
          <p>
            TO THE MAXIMUM EXTENT PERMITTED BY LAW, KAIZENIC SHALL NOT BE LIABLE
            FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE
            DAMAGES, INCLUDING BUT NOT LIMITED TO LOSS OF PROFITS, DATA, OR USE,
            ARISING FROM YOUR USE OF THE SERVICE.
          </p>

          <h3>9.4 Warranty Disclaimer</h3>
          <p>
            THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT
            WARRANTIES OF ANY KIND, EITHER EXPRESS OR IMPLIED, INCLUDING BUT NOT
            LIMITED TO WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR
            PURPOSE, OR NON-INFRINGEMENT.
          </p>
        </section>

        <section id="indemnification" class="terms-section">
          <h2>10. Indemnification</h2>
          <p>
            You agree to indemnify, defend, and hold harmless Kaizenic, its
            officers, directors, employees, and agents from and against any
            claims, damages, obligations, losses, liabilities, costs, or debt
            arising from your use of the Service, violation of these Terms, or
            violation of any third-party rights.
          </p>
        </section>

        <section id="termination" class="terms-section">
          <h2>11. Termination</h2>

          <h3>11.1 Termination by You</h3>
          <p>
            You may terminate your account and stop using the Service at any
            time by following the account deletion process in your settings.
          </p>

          <h3>11.2 Termination by Us</h3>
          <p>
            We may suspend or terminate your access to the Service immediately,
            without prior notice, if you violate these Terms, engage in
            fraudulent or illegal activities, or pose a security risk to the
            Service or other users.
          </p>

          <h3>11.3 Effect of Termination</h3>
          <p>
            Upon termination, your right to use the Service ceases immediately.
            We may delete your account and data as described in our Privacy
            Policy. Provisions that should survive termination will remain in
            effect.
          </p>
        </section>

        <section id="governing-law" class="terms-section">
          <h2>12. Governing Law and Dispute Resolution</h2>

          <h3>12.1 Governing Law</h3>
          <p>
            These Terms are governed by and construed in accordance with the
            laws of the United States and the State of Delaware, without regard
            to conflict of law principles.
          </p>

          <h3>12.2 Mandatory Arbitration</h3>
          <p>
            <strong
              >PLEASE READ THIS SECTION CAREFULLY. IT AFFECTS YOUR RIGHTS AND
              INCLUDES A CLASS ACTION WAIVER.</strong
            >
          </p>
          <p>
            You and Kaizenic agree that any dispute, claim, or controversy
            arising out of or relating to these Terms or your use of the Service
            will be settled by binding arbitration, rather than in court. The
            arbitration will be conducted by the American Arbitration
            Association ("AAA") under its Consumer Arbitration Rules. You and
            Kaizenic agree that each may bring claims against the other only in
            an individual capacity, not as a plaintiff or class member in any
            purported class proceeding.
          </p>
        </section>

        <section id="general-provisions" class="terms-section">
          <h2>13. General Provisions</h2>

          <h3>13.1 Entire Agreement</h3>
          <p>
            These Terms, together with our Privacy Policy, constitute the entire
            agreement between you and Kaizenic regarding the Service.
          </p>

          <h3>13.2 Severability</h3>
          <p>
            If any provision of these Terms is found to be unenforceable, the
            remaining provisions will remain in full force and effect.
          </p>

          <h3>13.3 Updates to Terms</h3>
          <p>
            We may update these Terms from time to time. We will notify you of
            material changes via email or prominent notice in the Service. Your
            continued use after such notice constitutes acceptance of the
            updated Terms.
          </p>
        </section>

        <section id="contact-legal" class="terms-section">
          <h2>14. Contact Information</h2>
          <p>For questions about these Terms of Use or legal inquiries:</p>
          <p>
            <strong>Email:</strong>
            <a href="mailto:legal&#64;kaizenic.ai">legal&#64;kaizenic.ai</a
            ><br />
            <strong>Response Time:</strong> We respond to legal inquiries within
            5 business days
          </p>
          <p>
            For privacy-related inquiries:
            <a href="mailto:privacy&#64;kaizenic.ai">privacy&#64;kaizenic.ai</a>
          </p>
        </section>

        <footer class="terms-footer">
          <p>
            <strong>Last Updated:</strong> August 4, 2025 &nbsp;·&nbsp;
            <strong>Version:</strong> 1.0
          </p>
          <p>
            For the most current version of these Terms, please visit:
            <a href="https://kaizenic.ai/terms">kaizenic.ai/terms</a>
          </p>
        </footer>
      </main>
    </div>
  `,
  styleUrl: './terms.component.scss',
})
export class TermsComponent implements OnInit {
  private readonly platformId = inject(PLATFORM_ID);

  ngOnInit(): void {
    if (isPlatformBrowser(this.platformId)) {
      window.scrollTo(0, 0);
    }
  }
}
